import { SkipLogicCondition } from "@common/components/skip-logic/skip-logic.types";
import { SpreadsheetImportType } from "./import-type";
import { InternalParentFieldList } from "@modules/spreadsheets/pages/spreadsheet-details/components/spreadsheet-management/spreadsheet-management.types";
import { SpreadsheetTableColumnType, SpreadsheetTableWhereClause } from "@modules/spreadsheets/pages/spreadsheet-details/components/spreadsheet-result/components/table/table.types";

export enum SpreadsheetType {
  individual = 'individual',
  pedigree = 'pedigree',
}

export type ResponseFieldType = {
  typeid: number;
  typename?: string;
  imageURL?: string;
  computed?: boolean;
  systemType?: boolean;
};

export type ColumnField = {
  fieldid: number;
  fieldname: string;
  system: boolean;
  fieldcontext: string;
  type: number;
  imageURL: string;
  fieldnameWithPath?: string;
  permissions?: string;
  tableIndex?: number;
  defaultWidth?: number;
  fieldClass?: string;
  systemDataField?: boolean;
  required?: boolean;
  dataSourceNodeId?: number;
  fieldtype?: ResponseFieldType;
  dropdownValues?: string[];
  lookupValues?: Record<string, string[]> | {};
};

export interface SmartColumn {
  field: ColumnField;
  columnOrder: number;
  sortOrder: number;
  sortAsc: boolean;
  editable?: boolean;
  securityEditable?: boolean;
  width: number;
  visible?: boolean;
  fieldtype?: ResponseFieldType;
  tableIndex?: number;
  fieldClass?: string;
  columnCount?: number;
  modulo?: number;
  parentField?: ColumnField;
  fieldnameWithPath?: string;
}
  
export enum SpreadsheetFormatType {
  defaultIndividual = "defaultIdsSsFormatId",
  defaultPedigree = "defaultPdsSsFormatId",
}

export type SpreadsheetColumnModelDto = {
  smartColumns: SmartColumn[];
};

export type SpreadsheetImportSettings = {
  pss: boolean;
  delimiter: string;
  numberOfUniqueIdentifiers: number;
  importIntoTablesOnly: boolean;
  importBlankCellsOption: number;
  uniqueImportNumber: number;
  columnModel: SpreadsheetColumnModelDto;
};

export type SpreadsheetDetailsImportDataParams = {
  fileData: File;
  delimiter?: string;
  importType: SpreadsheetImportType;
  nbMatchedColumns: number;
  ignoreHeaders?: boolean;
  settings?: SpreadsheetImportSettings;
};

export type SaveFormatPayload = {
  context: number;
  ssFormat: {
    id: number;
    name: string;
    folderId: number;
    description?: string;
  };
  columnModel: {
    smartColumns: SmartColumn[];
  };
  queryModel: {
    whereClauses: SpreadsheetTableWhereClause[];
  };
};

export type runFormatPayload = {
  context: number;
  folderId?: number;
  familyId?: number;
  ssFormat: {
    id: number;
    name: string;
    folderId: number;
    description?: string;
  };
  columnModel: {
    smartColumns: Array<{
      width: number;
      sortOrder: number;
      sortAsc: boolean;
      columnOrder: number;
      field: {
        fieldid: number;
        fieldname: string;
        system: boolean;
        fieldnameWithPath: string;
        fieldcontext: string;
        type: number;
        imageURL: string;
      };
      parentField?: ColumnField;
    }>;
  };
  queryModel: {
    whereClauses: SpreadsheetTableWhereClause[];
  };
  filter?: {
    whereClauses: SpreadsheetTableWhereClause[];
    andOR?: number;
  };
};

export type FormatManagementControllerProps = {
  spreadsheetType: SpreadsheetType;
  queryBuilderConditions?: SkipLogicCondition[];
  showErrorMessage: (message: string) => void;
  currentFormat?: {
    id?: number;
    name?: string;
    description?: string;
    folderId?: number;
  };
};

export type FormatInfo = {
  id: number;
  name: string;
  description?: string;
  folderId: number;
};

export type FormatDetails = {
  folderId: number;
  name: string;
  description?: string;
};

export type FormatInfoWrapper = {
  format?: FormatInfo;
};

export interface DeleteSsRowResponse {
  code: number;
  data: string;
  errors: string;
}

export type SSFormatFoldersDto = {
  id: number;
  folderId: number;
  name: string;
};

export type Field = {
  fieldid: number;
  fieldname: string;
  permissions: string;
  tableIndex: number;
  defaultWidth: number;
  fieldClass: string;
  system: boolean;
  systemDataField: boolean;
  required: boolean;
  fieldnameWithPath: string | null;
  fieldcontext: string;
  dataSourceNodeId: number;
  fieldtype: ResponseFieldType;
  dropdownValues: string[];
};

export type CellData = {
  modified: boolean;
  valid: boolean;
  rawValue: number | string | null; // depends on usage
  dataValue: number | string | null; // depends on usage
  updateValue: number | string | null; // depends on usage
  constraintViolation: string | null;
  displayValue?: string | null; // Optional: display value for dropdown fields (backend-provided)
  field: Field;
  rrn?: number;
  isTableData?: boolean;
  newRow?: boolean;
  tableId?: number;
};

export type CellUpdatePayload = {
  globalId: number;
  fieldId: number;
  context: number;
  systemField: boolean;
  ds: boolean;
  value: string | number;
  rrn: number;
  tableId: number;
};

// Spreadsheet Format Types
export interface SSFormatDTO {
  id: number;
  name: string;
  modifiedby: string;
  lastmodified: string;
  foldername: string;
  description: string;
  createdBy: string;
  createdDate: string;
  folderId: number;
}

export interface LoadISSFormatResponseDto {
  columnModel: Record<string, unknown>;
  queryModel: Record<string, unknown>;
  formatDetails: SSFormatDTO;
}
